// Pagination Component

/**
 * Render pagination controls
 * @param {Object} props - Pagination properties
 * @param {number} props.page - Current page (1-indexed)
 * @param {number} props.pages - Total pages
 * @param {number} props.total - Total items
 * @param {number} props.size - Items per page
 * @returns {string} HTML markup
 */
export function renderPagination({ page, pages, total, size }) {
  if (pages <= 1) return '';

  const pageNumbers = getPageNumbers(page, pages);

  return `
    <div class="pagination">
      <div class="pagination-info">
        ${total} Einträge | Seite ${page} von ${pages}
      </div>
      <div class="pagination-controls">
        <button class="pagination-btn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        ${pageNumbers.map(num => {
          if (num === '...') {
            return '<span class="pagination-ellipsis">...</span>';
          }
          return `<button class="pagination-btn ${num === page ? 'active' : ''}" data-page="${num}">${num}</button>`;
        }).join('')}
        <button class="pagination-btn" data-page="${page + 1}" ${page >= pages ? 'disabled' : ''}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      <div class="pagination-size">
        <label>Pro Seite:</label>
        <select id="page-size-select">
          <option value="10" ${size === 10 ? 'selected' : ''}>10</option>
          <option value="20" ${size === 20 ? 'selected' : ''}>20</option>
          <option value="50" ${size === 50 ? 'selected' : ''}>50</option>
          <option value="100" ${size === 100 ? 'selected' : ''}>100</option>
        </select>
      </div>
    </div>
  `;
}

/**
 * Get page numbers to display with ellipsis
 * @param {number} current - Current page
 * @param {number} total - Total pages
 * @returns {Array} Page numbers and ellipsis markers
 */
function getPageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = [];

  // Always show first page
  pages.push(1);

  if (current > 3) {
    pages.push('...');
  }

  // Show pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (current < total - 2) {
    pages.push('...');
  }

  // Always show last page
  if (!pages.includes(total)) {
    pages.push(total);
  }

  return pages;
}

/**
 * Initialize pagination event handlers
 * @param {Function} onPageChange - Callback for page changes (page, size)
 */
export function initPagination(onPageChange) {
  // Page button clicks
  document.querySelectorAll('.pagination-btn[data-page]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const page = parseInt(e.currentTarget.dataset.page, 10);
      const size = parseInt(document.getElementById('page-size-select')?.value || '20', 10);
      if (!isNaN(page) && !e.currentTarget.disabled) {
        onPageChange(page, size);
      }
    });
  });

  // Page size change
  document.getElementById('page-size-select')?.addEventListener('change', (e) => {
    const size = parseInt(e.target.value, 10);
    onPageChange(1, size); // Reset to page 1 when size changes
  });
}
